import { auth } from "@/components/context/firebase";
import { requestLanguage } from "@/lib/i18n";
import {
  captureMonitoringError,
  captureMonitoringMessage,
} from "@/lib/monitoring";

const DISABLE_AUTH = String(process.env.NEXT_PUBLIC_DISABLE_AUTH || "").toLowerCase() === "true";
// Dev-only: with Firebase auth bypassed locally, identify the recruiter to the
// backend via X-Dev-Uid (paired with backend DEV_AUTH_BYPASS=1). Mirrors the
// debug uid used in ContextGate. Never sent in prod (DISABLE_AUTH is false).
const DEBUG_UID = process.env.NEXT_PUBLIC_DEBUG_UID || "debug-local-hr";
const DEBUG_EMAIL = process.env.NEXT_PUBLIC_DEBUG_EMAIL || "";
const PYTHON_API_HOST = "python.aiinterviewagents.com";

const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000];
const SLOW_REQUEST_THRESHOLD_MS = Number(process.env.NEXT_PUBLIC_SLOW_REQUEST_MS) || 5000;
const DEFAULT_TIMEOUT_MS = Number(process.env.NEXT_PUBLIC_FETCH_TIMEOUT_MS) || 15_000;

// Polyfill for browsers that lack AbortSignal.any (pre-Chrome 116 / Safari 17.4)
const abortSignalAny =
  typeof AbortSignal.any === "function"
    ? (signals) => AbortSignal.any(signals)
    : (signals) => {
        const controller = new AbortController();
        for (const sig of signals) {
          if (sig.aborted) {
            controller.abort(sig.reason);
            return controller.signal;
          }
          sig.addEventListener("abort", () => controller.abort(sig.reason), { once: true });
        }
        return controller.signal;
      };

function sanitizeRequestUrl(url) {
  if (typeof url !== "string") return url;
  const trimmed = url.trim();

  if (
    (trimmed.startsWith("\"") && trimmed.endsWith("\"")) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }

  return trimmed;
}

function rewriteToProxyIfNeeded(url) {
  if (typeof window === "undefined") return url;
  if (typeof url !== "string") return url;

  if (url.startsWith("/api/proxy/")) return url;

  try {
    const parsed = new URL(url, window.location.origin);
    const host = parsed.host;
    const suffix = `${parsed.pathname}${parsed.search}`;

    if (host === "python.backend.aiinterviewagents.com" || host === "python.aiinterviewagents.com") {
      return `/api/proxy/python${suffix}`;
    }
  } catch {
    return url;
  }

  return url;
}

function appendUidQuery(url, uid) {
  if (!uid || typeof url !== "string") return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}uid=${uid}`;
}

async function getAuthToken() {
  if (DISABLE_AUTH) {
    return { token: null, uid: null };
  }
  const user = auth.currentUser;
  if (user) {
    return { token: await user.getIdToken(), uid: user.uid };
  }
  return { token: null, uid: null };
}

async function fetchWithAuth(url, options = {}) {
  const sanitizedUrl = sanitizeRequestUrl(url);
  const { token, uid } = await getAuthToken();
  const method = options.method ? options.method.toUpperCase() : 'GET';
  const originalUrl = typeof sanitizedUrl === "string" ? sanitizedUrl : "";
  // `proxy: false` opts a call out of the same-origin `/api/proxy/python`
  // rewrite — the free tools call the backend origin directly so the
  // client-IP header the shared abuse-control ceiling relies on survives one
  // fewer hop. Every other caller is unaffected: rewriteToProxyIfNeeded still
  // runs by default when `proxy` is omitted.
  const useProxy = options.proxy !== false;
  const rewrittenUrl = useProxy ? rewriteToProxyIfNeeded(sanitizedUrl) : originalUrl;
  // `?uid=` auto-append is a legacy Java-backend convention; opt out via
  // `options.omitUid` (the new mobile backend reads uid from the Firebase
  // token, not from the query string, and Pydantic's `extra="forbid"`
  // mode would reject the param).
  let finalUrl = (method === "GET" && !options.omitUid) ? appendUidQuery(rewrittenUrl, uid) : rewrittenUrl;

  const externalSignal = options.signal;
  const timeoutMs = Number.isFinite(options.timeoutMs) && options.timeoutMs > 0
    ? options.timeoutMs
    : DEFAULT_TIMEOUT_MS;

  const headers = {
    // The portal language, so backend messages come back in it (docs/MESSAGES_I18N.md).
    "Accept-Language": requestLanguage(),
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(DISABLE_AUTH ? { "X-Dev-Uid": DEBUG_UID, ...(DEBUG_EMAIL ? { "X-Dev-Email": DEBUG_EMAIL } : {}) } : {}),
  };

  // Build fetch options without the caller's signal — we manage abort below.
  // `retry: false` opts out of the retry loop entirely (single attempt) —
  // e.g. a free-tools generation call paired with an idempotency key, where
  // a silent internal retry would be indistinguishable from a second
  // explicit attempt. Every other caller is unaffected: MAX_RETRIES stays
  // the default when `retry` is omitted.
  const { signal: _ignored, timeoutMs: _ignoredTimeout, retry, proxy: _ignoredProxy, ...restOptions } = options;
  const maxRetries = retry === false ? 0 : MAX_RETRIES;
  const baseFetchOptions = {
    ...restOptions,
    method,
    headers,
  };

  // A `retry: false` caller (idempotency-keyed generation calls) must get
  // exactly one HTTP attempt — the proxy→direct fallback below is a second,
  // silent attempt against a different URL and would defeat that guarantee.
  const isPythonProxyCall =
    retry !== false &&
    useProxy &&
    typeof finalUrl === "string" &&
    finalUrl.startsWith("/api/proxy/python") &&
    typeof originalUrl === "string" &&
    originalUrl.includes(PYTHON_API_HOST);

  let lastResponse;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Per-attempt timeout — fresh controller each retry
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

    // Combine caller's signal (component unmount) + per-attempt timeout
    const signals = [timeoutController.signal];
    if (externalSignal) signals.push(externalSignal);
    const combinedSignal = abortSignalAny(signals);

    const fetchOptions = { ...baseFetchOptions, signal: combinedSignal };

    try {
      const requestStart = performance.now();
      let response = await fetch(finalUrl, fetchOptions);
      const durationMs = Math.round(performance.now() - requestStart);
      clearTimeout(timeoutId);

      if (durationMs > SLOW_REQUEST_THRESHOLD_MS) {
        captureMonitoringMessage(
          "api.request.slow",
          { url: finalUrl, method, durationMs, status: response.status },
          "warning"
        );
      }

      if (isPythonProxyCall && response.status >= 500) {
        const directUrl = method === "GET" ? appendUidQuery(originalUrl, uid) : originalUrl;
        try {
          const directResponse = await fetch(directUrl, fetchOptions);
          if (directResponse.ok || directResponse.status < 500) {
            captureMonitoringMessage(
              "api.request.proxy_fallback.success",
              {
                proxyUrl: finalUrl,
                directUrl,
                method,
                proxyStatus: response.status,
                directStatus: directResponse.status,
              },
              "warning"
            );
            response = directResponse;
          }
        } catch (fallbackError) {
          if (fallbackError.name === "AbortError") throw fallbackError;
          captureMonitoringError(fallbackError, {
            source: "fetchWithAuth.proxyFallback",
            proxyUrl: finalUrl,
            directUrl,
            method,
          });
        }
      }

      lastResponse = response;

      if (RETRYABLE_STATUSES.has(response.status) && attempt < maxRetries) {
        captureMonitoringMessage(
          "api.request.retry",
          { url: finalUrl, method, status: response.status, attempt: attempt + 1 },
          "warning"
        );
        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
        continue;
      }

      if (!response.ok && response.status >= 500) {
        captureMonitoringMessage(
          "api.request.server_error",
          { url: finalUrl, method, status: response.status },
          "error"
        );
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      // If the caller aborted (component unmount), propagate immediately — no retry
      if (externalSignal?.aborted) {
        throw error;
      }

      // If our timeout fired, log it and decide whether to retry
      if (timeoutController.signal.aborted) {
        captureMonitoringMessage(
          "api.request.timeout",
          { url: finalUrl, method, attempt: attempt + 1, timeoutMs },
          "warning"
        );
      }

      if (attempt < maxRetries) {
        captureMonitoringMessage(
          "api.request.retry",
          { url: finalUrl, method, status: "network_error", attempt: attempt + 1 },
          "warning"
        );
        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
        continue;
      }
      captureMonitoringError(error, {
        source: "fetchWithAuth",
        url: finalUrl,
        method,
      });
      throw error;
    }
  }

  return lastResponse;
}

export default fetchWithAuth;
